import getConfig from 'next/config';
import
{
    PenlyPlayPensionChoicesEnum,
    PenlyPlayPensionAgeGainChoicesEnum,
    PenlyPlayPensionDepositGainChoicesEnum,
    PenlyPlayPensionPaymentGainChoicesEnum,
    PenlyPlayPensionAgeChoicesEnum,
    PenlyPlayPensionDepositChoicesEnum,
    PenlyPlayPensionPaymentChoicesEnum,
    PenlyPlayApiEndpoints,
} from 'utils/enums';

const { publicRuntimeConfig } = getConfig();

export function getPenlyPlayPensionGainStepTitle(state) {
    switch (state.pension.pensionChoice) {
        case PenlyPlayPensionChoicesEnum.Age:

            switch (state.pension.pensionAgeChoice) {
                case PenlyPlayPensionAgeChoicesEnum.LowerAge:
                    return 'Hvordan vil du opnå at gå tidligere på pension?';
                case PenlyPlayPensionAgeChoicesEnum.HigherAge:
                    return 'Hvad vil du opnå ved at gå senere på pension?';
                default:
                    return '';
            }

        case PenlyPlayPensionChoicesEnum.Deposit:

            switch (state.pension.pensionDepositChoice) {
                case PenlyPlayPensionDepositChoicesEnum.LowerDeposit:
                    return 'Hvad vil du opnå med at indbetale mindre?';
                case PenlyPlayPensionDepositChoicesEnum.HigherDeposit:
                    return 'Hvad vil du opnå med at indbetale mere?';
                default:
                    return '';
            }

        case PenlyPlayPensionChoicesEnum.Payment:

            switch (state.pension.pensionPaymentChoice) {
                case PenlyPlayPensionPaymentChoicesEnum.LowerPayment:
                    return 'Hvad vil du opnå med at få mindre udbetalt?';
                case PenlyPlayPensionPaymentChoicesEnum.HigherPayment:
                    return 'Hvordan vil du opnå at få mere udbetalt?';
                default:
                    return '';
            }

        case PenlyPlayPensionChoicesEnum.Savings:

            switch (state.pension.pensionSavingsChoice) {
                case PenlyPlayPensionPaymentChoicesEnum.LowerPayment:
                    return 'Hvad vil du opnå med en mindre opsparing?';
                case PenlyPlayPensionPaymentChoicesEnum.HigherPayment:
                    return 'Hvordan vil du opnå at få en større opsparing?';
                default:
                    return '';
            }
        default:
            return '';
    }
}

export function getPenlyPlayPensionResultStepTitle(state) {
    switch (state.pension.pensionChoice) {
        case PenlyPlayPensionChoicesEnum.Age:

            switch (state.pension.pensionAgeGainChoice) {
                case PenlyPlayPensionAgeGainChoicesEnum.LowerAgeIncreaseDeposit:
                case PenlyPlayPensionAgeGainChoicesEnum.HigherAgeDecreaseDeposit:
                    return 'Indbetalt til pension per måned';
                case PenlyPlayPensionAgeGainChoicesEnum.LowerAgeDecreasePayment:
                case PenlyPlayPensionAgeGainChoicesEnum.HigherAgeIncreasePayment:
                    return 'Udbetalt i pension per måned';
                default:
                    return '';
            }

        case PenlyPlayPensionChoicesEnum.Deposit:

            switch (state.pension.pensionDepositGainChoice) {
                case PenlyPlayPensionDepositGainChoicesEnum.LowerDepositLowerPayment:
                    return 'Udbetalt per måned';
                case PenlyPlayPensionDepositGainChoicesEnum.LowerDepositHigherAge:
                    return 'Pensionsalder';
                case PenlyPlayPensionDepositGainChoicesEnum.HigherDepositHigherPayment:
                    return 'Udbetalt per måned';
                case PenlyPlayPensionDepositGainChoicesEnum.HigherDepositLowerAge:
                    return 'Pensionsalder';
                default:
                    return '';
            }

        case PenlyPlayPensionChoicesEnum.Payment:

            switch (state.pension.pensionPaymentGainChoice) {
                case PenlyPlayPensionPaymentGainChoicesEnum.LowerPaymentLowerDeposit:
                    return 'Indbetalt per måned';
                case PenlyPlayPensionPaymentGainChoicesEnum.LowerPaymentLowerAge:
                    return 'Pensionsalder';
                case PenlyPlayPensionPaymentGainChoicesEnum.HigherPaymentHigherDeposit:
                    return 'Indbetalt per måned';
                case PenlyPlayPensionPaymentGainChoicesEnum.HigherPaymentHigherAge:
                    return 'Pensionsalder';
                default:
                    return '';
            }
        default:
            return '';
    }
}

export function getEstimatedPensionAge(age, birthDateValue) {

    // Dates and ages from Star.dk
    const dates = [
        {
            date: new Date(1954, 6, 1),
            pensionAge: 66,
        },
        {
            date: new Date(1955, 1, 1),
            pensionAge: 66.5,
        },
        {
            date: new Date(1955, 6, 1),
            pensionAge: 67,
        },
        {
            date: new Date(1963, 1, 1),
            pensionAge: 68,
        },
        {
            date: new Date(1967, 1, 1),
            pensionAge: 69,
        },
        {
            date: new Date(1971, 1, 1),
            pensionAge: 70,
        },
        {
            date: new Date(1975, 1, 1),
            pensionAge: 71,
        },
        {
            date: new Date(1979, 1, 1),
            pensionAge: 72,
        },
        {
            date: new Date(1983, 1, 1),
            pensionAge: 72.5,
        },
        {
            date: new Date(1987, 6, 1),
            pensionAge: 73,
        },
        {
            date: new Date(1992, 1, 1),
            pensionAge: 73.5,
        },
        {
            date: new Date(1996, 6, 1),
            pensionAge: 74,
        },
    ];

    let birthDate = null;

    if (!birthDateValue && age) {
        const birthYear = new Date().getFullYear() - age;
        birthDate = new Date(birthYear, 6, 1);
    }

    let pensionAgeEstimated = dates[0];

    dates.forEach((date) => {
        if (birthDate > date.date) {
            pensionAgeEstimated = date;
        }
    });

    return pensionAgeEstimated.pensionAge;
}

export async function getCurrentCalculation(penlyPlayState) {
    const pensionAge = parseInt(penlyPlayState.pension.pensionAge ?? penlyPlayState.pension.pensionAgeCurrent, 0);
    const pensionYear = new Date().getFullYear() + (pensionAge - penlyPlayState.basic.age);
    let startYear = new Date().getFullYear();
    const pensionYearCurrent = new Date().getFullYear() + (parseInt(penlyPlayState.pension.pensionAgeCurrent, 0) - penlyPlayState.basic.age);

    // TODO: The person is already retired per the statistics. How should we handle this?
    if (pensionYear <= startYear) {
        startYear = pensionYear-1;
    }

    const calculationValues = {
        'startYear': startYear,
        'endYear': pensionYearCurrent + 20,
        'startPrincipal': parseFloat(penlyPlayState.pension.pensionSavedToDate, 0),
        'savingsInterestRate': {
            'asFraction': 0.05,
        },
        'savingsInflationRate': {
            'asFraction': 0.015,
        },
        'pensionsInterestRate': {
            'asFraction': 0.03,
        },
        'pensionsInflationRate': {
            'asFraction': 0.015,
        },
        'deflateOutput': true,
        'filter':
        ['Balance', 'Primo'],
    };

    calculationValues.pensionYear = pensionYear;
    calculationValues.savingsInstallment = penlyPlayState.pension.depositPerMonthCurrent * 12;

    const endPoint = PenlyPlayApiEndpoints.PaymentEndPoint;
    const calculationResult = await fetch(`${publicRuntimeConfig.penlyPlay.calculationApiUrl}${endPoint}`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json; v=2.0',
            'X-Api-Key': publicRuntimeConfig.penlyPlay.apiKey,
        },
        body: JSON.stringify(calculationValues),
    });

    return calculationResult
        .json()
        .then((result) => {
            if (result.status && (result.status <= 200 || result.status >= 299)) {
                return null;
            }

            const pensionDevelopment = [];
            let currentYear = new Date().getFullYear();
            let currentAge = parseInt(penlyPlayState.basic.age, 0);
            let savingsAtPension = 0;
            result.result.prognosis.forEach((value) => {
                if (value.type === 'Balance' && value.date.primoUltimo === 'Primo') {
                    const { amount } = value;
                    const year = {
                        'year': currentYear,
                        'age': currentAge,
                        'value': amount,
                    };
                    pensionDevelopment.push(year);
                    currentYear += 1;
                    currentAge += 1;

                    if (amount > savingsAtPension) {
                        savingsAtPension = amount;
                    }
                }
            });

            const paymentPerMonth = (result.result.firstPensionInstallment / 12) * -1;

            return {
                pensionDevelopment,
                savingsAtPension,
                paymentPerMonth,
                calculationValues:
                {
                    firstPensionInstallment: (result.result.firstPensionInstallment * -1),
                },
            };
        });
}

export async function getPaymentCalculation(penlyPlayState) {

    const pensionAge = parseInt(penlyPlayState.pension.pensionAgeCalculated ?? penlyPlayState.pension.pensionAgeCurrent, 0);
    const pensionYear = new Date().getFullYear() + (pensionAge - penlyPlayState.basic.age);
    let startYear = new Date().getFullYear();
    const pensionYearCurrent = new Date().getFullYear() + (parseInt(penlyPlayState.pension.pensionAgeCurrent, 0) - penlyPlayState.basic.age);

    // TODO: The person is already retired per the statistics. How should we handle this?
    if (pensionYear <= startYear) {
        startYear = pensionYear-1;
    }

    const calculationValues = {
        'startYear': startYear,
        'endYear': pensionYearCurrent + 20,
        'startPrincipal': parseFloat(penlyPlayState.pension.pensionSavedToDate, 0),
        'savingsInterestRate': {
            'asFraction': 0.05,
        },
        'savingsInflationRate': {
            'asFraction': 0.015,
        },
        'pensionsInterestRate': {
            'asFraction': 0.03,
        },
        'pensionsInflationRate': {
            'asFraction': 0.015,
        },
        'deflateOutput': true,
        'filter':
        ['Balance', 'Primo'],
    };

    calculationValues.pensionYear = pensionYear;
    calculationValues.savingsInstallment = (penlyPlayState.pension.depositPerMonthCalculated ?? penlyPlayState.pension.depositPerMonthCurrent) * 12;

    const endPoint = PenlyPlayApiEndpoints.PaymentEndPoint;
    const calculationResult = await fetch(`${publicRuntimeConfig.penlyPlay.calculationApiUrl}${endPoint}`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json; v=2.0',
            'X-Api-Key': publicRuntimeConfig.penlyPlay.apiKey,
        },
        body: JSON.stringify(calculationValues),
    });

    return calculationResult
        .json()
        .then((result) => {
            const pensionDevelopment = [];
            let currentYear = new Date().getFullYear();
            let currentAge = parseInt(penlyPlayState.basic.age, 0);
            let savingsAtPension = 0;

            result.result.prognosis.forEach((value) => {
                if (value.type === 'Balance' && value.date.primoUltimo === 'Primo') {
                    const { amount } = value;
                    const year = {
                        'year': currentYear,
                        'age': currentAge,
                        'value': amount,
                    };
                    pensionDevelopment.push(year);
                    currentYear += 1;
                    currentAge += 1;

                    if (amount > savingsAtPension) {
                        savingsAtPension = amount;
                    }
                }
            });

            const paymentPerMonth = (result.result.firstPensionInstallment / 12) * -1;

            return {
                pensionDevelopment,
                savingsAtPension,
                paymentPerMonth,
                calculationValues:
                {
                    firstPensionInstallment: (result.result.firstPensionInstallment * -1),
                },
            };
        });
}

export async function getDepositCalculation(penlyPlayState) {

    const pensionAge = parseInt(penlyPlayState.pension.pensionAgeCalculated ?? penlyPlayState.pension.pensionAgeCurrent, 0);
    const pensionYear = new Date().getFullYear() + (pensionAge - penlyPlayState.basic.age);
    let startYear = new Date().getFullYear();
    const pensionYearCurrent = new Date().getFullYear() + (parseInt(penlyPlayState.pension.pensionAgeCurrent, 0) - penlyPlayState.basic.age);

    // TODO: The person is already retired per the statistics. How should we handle this?
    if (pensionYear <= startYear) {
        startYear = pensionYear-1;
    }

    const calculationValues = {
        'startYear': startYear,
        'endYear': pensionYearCurrent + 20,
        'startPrincipal': parseFloat(penlyPlayState.pension.pensionSavedToDate, 0),
        'savingsInterestRate': {
            'asFraction': 0.05,
        },
        'savingsInflationRate': {
            'asFraction': 0.015,
        },
        'pensionsInterestRate': {
            'asFraction': 0.03,
        },
        'pensionsInflationRate': {
            'asFraction': 0.015,
        },
        'deflateOutput': true,
        'filter':
        ['Balance', 'Primo'],
    };

    let payment = penlyPlayState.pension.calculationValues.firstPensionInstallment;

    if (penlyPlayState.pension.paymentPerMonthCalculated) {
        payment = penlyPlayState.pension.paymentPerMonthCalculated * 12;
    }

    calculationValues.pensionYear = pensionYear;
    calculationValues.pensionInstallmentPresentValue = payment;

    const endPoint = PenlyPlayApiEndpoints.DepositEndPoint;
    const calculationResult = await fetch(`${publicRuntimeConfig.penlyPlay.calculationApiUrl}${endPoint}`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json; v=2.0',
            'X-Api-Key': publicRuntimeConfig.penlyPlay.apiKey,
        },
        body: JSON.stringify(calculationValues),
    });

    return calculationResult
        .json()
        .then((result) => {
            const pensionDevelopment = [];
            let currentYear = new Date().getFullYear();
            let currentAge = parseInt(penlyPlayState.basic.age, 0);
            let savingsAtPension = 0;

            result.result.prognosis.forEach((value) => {
                if (value.type === 'Balance' && value.date.primoUltimo === 'Primo') {
                    const { amount } = value;
                    const year = {
                        'year': currentYear,
                        'age': currentAge,
                        'value': amount,
                    };
                    pensionDevelopment.push(year);
                    currentYear += 1;
                    currentAge += 1;

                    if (amount > savingsAtPension) {
                        savingsAtPension = amount;
                    }
                }
            });

            const depositPerMonth = result.result.firstSavingsInstallment / 12;

            return {
                pensionDevelopment,
                savingsAtPension,
                depositPerMonth,
            };
        });
}

export async function getAgeCalculation(penlyPlayState) {

    const pensionAge = parseInt(penlyPlayState.pension.pensionAge ?? penlyPlayState.pension.pensionAgeCurrent, 0);
    const pensionYear = new Date().getFullYear() + (pensionAge - penlyPlayState.basic.age);
    let startYear = new Date().getFullYear();
    const pensionYearCurrent = new Date().getFullYear() + (parseInt(penlyPlayState.pension.pensionAgeCurrent, 0) - penlyPlayState.basic.age);

    // TODO: The person is already retired per the statistics. How should we handle this?
    if (pensionYear <= startYear) {
        startYear = pensionYear-1;
    }

    const calculationValues = {
        'startYear': startYear,
        'endYear': pensionYearCurrent + 20,
        'startPrincipal': parseFloat(penlyPlayState.pension.pensionSavedToDate, 0),
        'savingsInterestRate': {
            'asFraction': 0.05,
        },
        'inflationRate': {
            'asFraction': 0.015,
        },
        'pensionsInterestRate': {
            'asFraction': 0.03,
        },
        'deflateOutput': true,
        'filter':
        ['Balance', 'Primo'],
    };

    let payment = penlyPlayState.pension.calculationValues.firstPensionInstallment;

    if (penlyPlayState.pension.paymentPerMonthCalculated) {
        payment = penlyPlayState.pension.paymentPerMonthCalculated * 12;
    }

    calculationValues.savingsInstallment = (penlyPlayState.pension.depositPerMonthCalculated ?? penlyPlayState.pension.depositPerMonthCurrent) * 12;
    calculationValues.pensionInstallmentPresentValue = payment;

    const endPoint = PenlyPlayApiEndpoints.AgeEndPoint;
    const calculationResult = await fetch(`${publicRuntimeConfig.penlyPlay.calculationApiUrl}${endPoint}`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json; v=2.0',
            'X-Api-Key': publicRuntimeConfig.penlyPlay.apiKey,
        },
        body: JSON.stringify(calculationValues),
    });

    return calculationResult
        .json()
        .then((result) => {
            const pensionDevelopment = [];
            let currentYear = new Date().getFullYear();
            let currentAge = parseInt(penlyPlayState.basic.age, 0);
            let savingsAtPension = 0;

            result.result.prognosis.forEach((value) => {
                if (value.type === 'Balance' && value.date.primoUltimo === 'Primo') {
                    const { amount } = value;
                    const year = {
                        'year': currentYear,
                        'age': currentAge,
                        'value': amount,
                    };
                    pensionDevelopment.push(year);
                    currentYear += 1;
                    currentAge += 1;

                    if (amount > savingsAtPension) {
                        savingsAtPension = amount;
                    }
                }
            });

            const newPensionYear = result.result.pensionYear;
            const newPensionAge = currentAge + (newPensionYear - currentYear);

            return {
                pensionDevelopment,
                savingsAtPension,
                pensionAge: newPensionAge,
            };
        });
}

export const isAgeStateValid = (penlyPlayState) => (penlyPlayState.basic.age !== undefined && parseInt(penlyPlayState.basic.age, 0) > 18);

export const isSalaryBeforeTaxStateValid = (penlyPlayState) => penlyPlayState.basic.salaryBeforeTax !== undefined && parseInt(penlyPlayState.basic.salaryBeforeTax, 0) > 1;

export const isPensionSavedToDateStateValid = (penlyPlayState) => penlyPlayState.pension.pensionSavedToDate !== undefined && parseInt(penlyPlayState.pension.pensionSavedToDate, 0) > 0;

export const isPensionDepositPerMonthInPercentValid = (penlyPlayState) => penlyPlayState.pension.depositPerMonthInPercentCurrent !== undefined && parseInt(penlyPlayState.pension.depositPerMonthInPercentCurrent, 0) > 0;

export const isPensionInputStateValid = (penlyPlayState) => {
    // Basic validation
    const ageValidated = isAgeStateValid(penlyPlayState);
    const salaryBeforeTaxValidated = isSalaryBeforeTaxStateValid(penlyPlayState);

    // Pension validation
    const pensionSavedToDateValidated = isPensionSavedToDateStateValid(penlyPlayState);
    const depositPerMonthInPercentCurrentValidated = isPensionDepositPerMonthInPercentValid(penlyPlayState);
    return ageValidated && pensionSavedToDateValidated && depositPerMonthInPercentCurrentValidated && salaryBeforeTaxValidated;
};
